import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../components/AuthContext/AuthContext";
import ReusableAccordion from "../../stories/Accordions/ReusableAccordion";
import VitalityDuplicatedTable from "../../stories/Tables/VitalityDuplicatedTable";
import PageLayout from "../../components/Layout/PageLayout";
import ExitModal from "../../stories/Modals/ExitModal";
import {
  bodyMeasurementsColumns,
  bloodPressureColumns,
  lipidProfileColumns,
  glucoseColumns,
  getEnabledSections,
  MEASUREMENT_SECTIONS,
} from "../../stories/Forms/fields";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import Notes from "../../stories/Forms/Notes";
import { Button } from "../../stories/Buttons/Button";
import ExitConsultationButton from "../../stories/Buttons/ExitConsultationButton";
import { useLocation, useNavigate } from "react-router";
import VHConfirmationPopup from "../../stories/Modals/VHConfirmationPopup";
import { useConsultationDraft } from "../../hooks/useConsultationDraft";
import Spinner from "react-bootstrap/Spinner";
import { clinicalMeasurementsUrl } from "../../utils/constants";
import useApis from "../../services/useAPI";
import informationIcon from "../../assets/icons/Information-fill.svg";

import {
  getRangeError,
  validateTableData,
  stripId,
  addIds,
  toSingleObject,
  toSingleRowArray,
} from "../../utils/utils";

import { Form } from "react-bootstrap";

const ALLOWED_STAGES = ["clinical-screening", "clinical-measurement"];

const getBloodPressureError = (data, heartRhythmIrregular) => {
  const isFilled = (v) => v !== "" && v !== null && v !== undefined;

  const clinicRow = data.find((row) => row.id === 5);
  const readingRows = data.filter((row) => row.id !== 5);

  if (clinicRow) {
    const hasSys = isFilled(clinicRow.systolic);
    const hasDia = isFilled(clinicRow.diastolic);
    const hasHeartRate = isFilled(clinicRow.heartRate);

    const anyClinicFieldFilled =
      hasSys || hasDia || hasHeartRate;

    const clinicComplete =
      hasSys && hasDia && hasHeartRate;

    if (anyClinicFieldFilled && !clinicComplete) {
      return "Please enter the clinic BP reading";
    }
  }

  const anyReadingComplete =
    readingRows.some(
      (row) =>
        isFilled(row.systolic) ||
        isFilled(row.diastolic) ||
        isFilled(row.heartRate) ||
        isFilled(row.armUsed),
    ) || heartRhythmIrregular;

  if (anyReadingComplete && clinicRow) {
    const clinicComplete =
      isFilled(clinicRow.systolic) &&
      isFilled(clinicRow.diastolic) &&
      isFilled(clinicRow.heartRate);

    if (!clinicComplete) {
      return "Please enter the clinic BP reading";
    }
  }

  return null;
};
const INITIAL_BODY_MEASUREMENTS = [
  { id: 1, parameter: "Reading", height: "", weight: "", waist: "", bmi: "" },
];

const INITIAL_BLOOD_PRESSURE = [
  {
    id: 1,
    reading: "Reading 1",
    systolic: "",
    diastolic: "",
    heartRate: "",
    armUsed: "",
  },
  {
    id: 2,
    reading: "Reading 2",
    systolic: "",
    diastolic: "",
    heartRate: "",
    armUsed: "",
  },
  {
    id: 3,
    reading: "Reading 3 (if necessary)",
    systolic: "",
    diastolic: "",
    heartRate: "",
    armUsed: "",
  },
  {
    id: 4,
    reading: "Reading 4 (if necessary)",
    systolic: "",
    diastolic: "",
    heartRate: "",
    armUsed: "",
  },
  {
    id: 5,
    reading: "Clinic blood pressure",
    systolic: "",
    diastolic: "",
    heartRate: "",
  },
];

const INITIAL_LIPID_PROFILE = [
  {
    id: 1,
    totalCholesterol: "",
    hdlCholesterol: "",
    triglycerides: "",
    ldlCholesterol: "",
    totalHdlRatio: "",
    nonHdlCholesterol: "",
    heartAge: "",
    cvdRisk: "",
  },
];

const INITIAL_GLUCOSE = [{ id: 1, parameter: "Reading", glucose: "" }];

const ClinicalMeasurements = () => {
  const auth = useAuth();
  const {callApi} = useApis();
  const [notes, setNotes] = useState("");
  const navigate = useNavigate();
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [heartRhythmIrregular, setHeartRhythmIrregular] = useState(false);
  const [fingerPrickAttempts, setFingerPrickAttempts] = useState(null);
  const location = useLocation();
  const hasHydratedDraft = useRef(false);

  const bookingId = location.state?.bookingId;
  const serviceName = location.state?.serviceName;
  const patientSex = location.state?.patientSex;
  const serviceType = location.state?.serviceType;
  const displayUserInfo =
    location.state?.displayUserInfo ?? auth.displayUserInfo;
  const isEditMode = location?.state?.isEditMode ?? false;

  const enabledSections = getEnabledSections(serviceName); 
  const isSectionEnabled = (section) => enabledSections.includes(section);

  const toSingleObjectOmit = (arr = [], keysToOmit = []) => {
    const obj = toSingleObject(arr);
    const result = { ...obj };
    keysToOmit.forEach((key) => delete result[key]);
    return result;
  };

  const toSingleRowArrayWithDefaults = (obj, id = 1, defaults = {}) =>
    obj && Object.keys(obj).length ? [{ id, ...defaults, ...obj }] : null;

  const { draft, isLoaded, saveSlice } = useConsultationDraft(
    bookingId,
    serviceName,
    patientSex,
    serviceType,
  );
  const [bodyMeasurementsData, setBodyMeasurementsData] = useState(
    INITIAL_BODY_MEASUREMENTS,
  );
  const [bodyMeasurementsErrors, setBodyMeasurementsErrors] = useState({});

  const [bloodPressureData, setBloodPressureData] = useState(
    INITIAL_BLOOD_PRESSURE,
  );
  const [bloodPressureErrors, setBloodPressureErrors] = useState({});
  const bloodPressureError = getBloodPressureError(
    bloodPressureData,
    heartRhythmIrregular,
  );

  const [lipidProfileData, setLipidProfileData] = useState(
    INITIAL_LIPID_PROFILE,
  );
  const [lipidProfileErrors, setLipidProfileErrors] = useState({});

  const [glucoseData, setGlucoseData] = useState(INITIAL_GLUCOSE);
  const [glucoseErrors, setGlucoseErrors] = useState({});

  const buildBloodPressurePayload = (data) => ({
    readings: stripId(data.filter((row) => row.id !== 5)),
    clinical_reading: toSingleObjectOmit(
      data.filter((row) => row.id === 5),
      ["reading"],
    ),
  });

  useEffect(() => {
    if (
      !ALLOWED_STAGES.includes(sessionStorage.getItem("allowedStage")) &&
      auth?.featureFlag
    ) {
      navigate("/patient-record");
    }
  }, [auth?.featureFlag, navigate]);

  useEffect(() => {
    if (!isLoaded) return;

    const clinicalMeasurements = {};

    if (isSectionEnabled(MEASUREMENT_SECTIONS.BODY_MEASUREMENTS)) {
      clinicalMeasurements.body_measurements = toSingleObjectOmit(bodyMeasurementsData, ["parameter"]);
    }
    if (isSectionEnabled(MEASUREMENT_SECTIONS.BLOOD_PRESSURE)) {
      clinicalMeasurements.blood_pressure = buildBloodPressurePayload(bloodPressureData);
      clinicalMeasurements.heart_rhythm_irregular = heartRhythmIrregular;
    }
    if (isSectionEnabled(MEASUREMENT_SECTIONS.LIPID_PROFILE)) {
      clinicalMeasurements.lipid_profile = {
        ...toSingleObject(lipidProfileData),
        finger_prick_attempts: fingerPrickAttempts,
      };
    }
    if (isSectionEnabled(MEASUREMENT_SECTIONS.GLUCOSE)) {
      clinicalMeasurements.glucose = toSingleObjectOmit(glucoseData, ["parameter"]);
    }

    saveSlice({ clinical_measurements: clinicalMeasurements, notes });
  }, [
    bodyMeasurementsData,
    bloodPressureData,
    lipidProfileData,
    glucoseData,
    heartRhythmIrregular,
    fingerPrickAttempts,
    notes,
    isLoaded,
    serviceName,
  ]);

  const buildBloodPressureState = (bpPayload) => {
    if (!bpPayload) return INITIAL_BLOOD_PRESSURE;
    let readings = [];
    try {
      const candidate = addIds(
        Array.isArray(bpPayload.readings) ? bpPayload.readings : [],
        1,
      );
      readings = Array.isArray(candidate) ? candidate : [];
    } catch (e) {
      readings = [];
    }
    let clinicReading = [INITIAL_BLOOD_PRESSURE[4]];
    try {
      const candidate = toSingleRowArrayWithDefaults(
        bpPayload.clinical_reading,
        5,
        { reading: "Clinic blood pressure" },
      );
      clinicReading = Array.isArray(candidate)
        ? candidate
        : [INITIAL_BLOOD_PRESSURE[4]];
    } catch (e) {
      clinicReading = [INITIAL_BLOOD_PRESSURE[4]];
    }

    return [...readings, ...clinicReading];
  };

  useEffect(() => {
    if (!isLoaded || hasHydratedDraft.current) return;

    const measurements = draft?.clinical_measurements;

    if (measurements) {
      const bodyData =
        toSingleRowArrayWithDefaults(measurements.body_measurements, 1, {
          parameter: "Reading",
        }) ?? INITIAL_BODY_MEASUREMENTS;

      const bpData = buildBloodPressureState(measurements.blood_pressure);
      const { finger_prick_attempts: draftFingerPrick, ...lipidRow } =
        measurements.lipid_profile || {};
      const lipidData =
        toSingleRowArray(lipidRow, 1) ??
        INITIAL_LIPID_PROFILE;
      const glucoseDataLoaded =
        toSingleRowArrayWithDefaults(measurements.glucose, 1, {
          parameter: "Reading",
        }) ?? INITIAL_GLUCOSE;

      setBodyMeasurementsData(bodyData);
      setBloodPressureData(bpData);
      setLipidProfileData(lipidData);
      setGlucoseData(glucoseDataLoaded);
      setHeartRhythmIrregular(measurements.heart_rhythm_irregular || false);
      setFingerPrickAttempts(
        draftFingerPrick ??
          measurements.lipid_profile?.finger_prick_attempts ??
          null,
      );

      setBodyMeasurementsErrors(
        validateTableData(bodyData, bodyMeasurementsColumns),
      );
      setBloodPressureErrors(validateTableData(bpData, bloodPressureColumns));
      setLipidProfileErrors(validateTableData(lipidData, lipidProfileColumns));
      setGlucoseErrors(validateTableData(glucoseDataLoaded, glucoseColumns));
    }

    setNotes(draft?.notes || "");
    hasHydratedDraft.current = true;
  }, [isLoaded, draft]);

  const handleBodyMeasurementChange = (id, field, value) => {
    const column = bodyMeasurementsColumns.find((c) => c.dataField === field);
    const error = getRangeError(value, column?.min, column?.max);
    setBodyMeasurementsErrors((prev) => ({
      ...prev,
      [`${id}_${field}`]: error,
    }));

    setBodyMeasurementsData((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;

        const updatedRow = { ...row, [field]: value };

        if (field === "height" || field === "weight") {
          const height = parseFloat(updatedRow.height);
          const weight = parseFloat(updatedRow.weight);

          if (height > 0 && weight > 0) {
            const heightInMetres = height / 100;
            updatedRow.bmi = (
              weight /
              (heightInMetres * heightInMetres)
            ).toFixed(1);

            const bmiError = getRangeError(updatedRow.bmi, 10, 70);
            setBodyMeasurementsErrors((prev) => ({
              ...prev,
              [`${id}_bmi`]: bmiError,
            }));
          } else {
            updatedRow.bmi = "";
            setBodyMeasurementsErrors((prev) => ({
              ...prev,
              [`${id}_bmi`]: null,
            }));
          }
        }

        if (field === "height" || field === "waist") {
          const height = parseFloat(updatedRow.height);
          const waist = parseFloat(updatedRow.waist);

          if (height > 0 && waist > 0) {
            updatedRow.waistRatio = (waist / height).toFixed(2);
          } else {
            updatedRow.waistRatio = "";

            setBodyMeasurementsErrors((prev) => ({
              ...prev,
              [`${id}_waistRatio`]: null,
            }));
          }
        }

        return updatedRow;
      }),
    );
  };

  const handleBloodPressureChange = (id, field, value) => {
    const column = bloodPressureColumns.find((c) => c.dataField === field);
    const error = getRangeError(value, column?.min, column?.max);
    setBloodPressureErrors((prev) => ({
      ...prev,
      [`${id}_${field}`]: error,
    }));

    setBloodPressureData((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );
  };

  const handleLipidProfileChange = (id, field, value) => {
    const column = lipidProfileColumns.find((c) => c.dataField === field);
    const error = getRangeError(value, column?.min, column?.max);
    setLipidProfileErrors((prev) => ({
      ...prev,
      [`${id}_${field}`]: error,
    }));

    setLipidProfileData((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;

        const updatedRow = { ...row, [field]: value };

        if (
          field === "totalCholesterol" ||
          field === "hdlCholesterol" ||
          field === "triglycerides"
        ) {
          const totalCholesterol = parseFloat(updatedRow.totalCholesterol);
          const hdlCholesterol = parseFloat(updatedRow.hdlCholesterol);
          const triglycerides = parseFloat(updatedRow.triglycerides);

          // LDL Cholesterol
          if (
            !isNaN(totalCholesterol) &&
            !isNaN(hdlCholesterol) &&
            !isNaN(triglycerides)
          ) {
            updatedRow.ldlCholesterol = (
              totalCholesterol -
              hdlCholesterol -
              triglycerides / 2.2
            ).toFixed(1);

            const ldlError = getRangeError(updatedRow.ldlCholesterol, 0.1, 10);
            setLipidProfileErrors((prev) => ({
              ...prev,
              [`${id}_ldlCholesterol`]: ldlError,
            }));
          } else {
            updatedRow.ldlCholesterol = "";
            setLipidProfileErrors((prev) => ({
              ...prev,
              [`${id}_ldlCholesterol`]: null,
            }));
          }

          // Total / HDL Ratio
          if (
            !isNaN(totalCholesterol) &&
            !isNaN(hdlCholesterol) &&
            hdlCholesterol > 0
          ) {
            updatedRow.totalHdlRatio = (
              totalCholesterol / hdlCholesterol
            ).toFixed(1);

            const ratioError = getRangeError(updatedRow.totalHdlRatio, 1, 16);
            setLipidProfileErrors((prev) => ({
              ...prev,
              [`${id}_totalHdlRatio`]: ratioError,
            }));
          } else {
            updatedRow.totalHdlRatio = "";
            setLipidProfileErrors((prev) => ({
              ...prev,
              [`${id}_totalHdlRatio`]: null,
            }));
          }

          // Non-HDL Cholesterol
          if (!isNaN(totalCholesterol) && !isNaN(hdlCholesterol)) {
            updatedRow.nonHdlCholesterol = (
              totalCholesterol - hdlCholesterol
            ).toFixed(1);

            const nonHdlError = getRangeError(
              updatedRow.nonHdlCholesterol,
              0.1,
              10,
            );
            setLipidProfileErrors((prev) => ({
              ...prev,
              [`${id}_nonHdlCholesterol`]: nonHdlError,
            }));
          } else {
            updatedRow.nonHdlCholesterol = "";
            setLipidProfileErrors((prev) => ({
              ...prev,
              [`${id}_nonHdlCholesterol`]: null,
            }));
          }
        }

        return updatedRow;
      }),
    );
  };

  const handleGlucoseChange = (id, field, value) => {
    const column = glucoseColumns.find((c) => c.dataField === field);
    const error = getRangeError(value, column?.min, column?.max);
    setGlucoseErrors((prev) => ({
      ...prev,
      [`${id}_${field}`]: error,
    }));

    setGlucoseData((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );
  };

  const handleContinue = async() => {
    const result = await callApi(
      { booking_id: bookingId, service_name: serviceName, is_edit: isEditMode},
      clinicalMeasurementsUrl,
      "POST"
    );
    if (result?.success) {
    auth?.allowStage("clinical-measurement");
    navigate("/result-summary", {
      state: {
        bookingId,
        serviceType,
        serviceName,
        patientSex,
        displayUserInfo,
      },
    });}
  };
  const handleExitConsultation = () => {
    setIsExitModalOpen(true);
  };

  const handleCloseExitModal = () => {
    setIsExitModalOpen(false);
  };

  const handleConfirmExit = () => {
    setIsExitModalOpen(false);
  };

  const hasValidationErrors =
    (isSectionEnabled(MEASUREMENT_SECTIONS.BODY_MEASUREMENTS) &&
      Object.values(bodyMeasurementsErrors ?? {}).some(Boolean)) ||
    (isSectionEnabled(MEASUREMENT_SECTIONS.BLOOD_PRESSURE) &&
      Object.values(bloodPressureErrors ?? {}).some(Boolean)) ||
    (isSectionEnabled(MEASUREMENT_SECTIONS.LIPID_PROFILE) &&
      Object.values(lipidProfileErrors ?? {}).some(Boolean)) ||
    (isSectionEnabled(MEASUREMENT_SECTIONS.GLUCOSE) &&
      Object.values(glucoseErrors ?? {}).some(Boolean));

  const isDisabledContinue =
    isSectionEnabled(MEASUREMENT_SECTIONS.BLOOD_PRESSURE) && Boolean(bloodPressureError);

  if (!isLoaded) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" className="my-custom-spinner" />
      </div>
    );
  }

  return (
    <LoggedInLayout isVhlogin={true} vitalityLayout={true}>
      <ExitConsultationButton onClick={handleExitConsultation} />
      <ExitModal
        isOpen={isExitModalOpen}
        onClose={handleCloseExitModal}
        onConfirm={handleConfirmExit}
        bookingId={bookingId}
        notes={notes}
        onNotesChange={setNotes}
      />
      <PageLayout
        heading="Clinical measurements"
        className="clinical-measurement"
      >
        <div className="measurement-card">
          {isSectionEnabled(MEASUREMENT_SECTIONS.BODY_MEASUREMENTS) && (
          <ReusableAccordion
            sectionTitle="Body measurements"
            hasValidationErrors={Object.values(bodyMeasurementsErrors ?? {}).some(
              Boolean,
            )}
          >
            <VitalityDuplicatedTable
              data={bodyMeasurementsData}
              columns={bodyMeasurementsColumns}
              pagination={false}
              onFieldChange={handleBodyMeasurementChange}
              errors={bodyMeasurementsErrors}
            />
          </ReusableAccordion>
          )}

        {isSectionEnabled(MEASUREMENT_SECTIONS.BLOOD_PRESSURE) && (
          <ReusableAccordion
            className="blood-pressure-card"
            sectionTitle="Blood pressure & pulse"
            errorMessage={bloodPressureError}
            hasValidationErrors={Object.values(bloodPressureErrors ?? {}).some(
              Boolean,
            )}
          >
            <VitalityDuplicatedTable
              data={bloodPressureData}
              columns={bloodPressureColumns}
              pagination={false}
              onFieldChange={handleBloodPressureChange}
              errors={bloodPressureErrors}
              hideColumnForRow={(column, row) =>
                row.id === 5 && column.dataField === "armUsed"
              }
            />

            <div className="blood-pressure-info">
              <img src={informationIcon} alt="" />
              <span>
                Record the lower of the last 2 measurements as the clinic blood pressure.
              </span>
            </div>

            <Form.Check
              type="checkbox"
              id="heart-rhythm-irregular"
              className="heart-rhythm-checkbox"
              checked={heartRhythmIrregular}
              onChange={(e) => setHeartRhythmIrregular(e.target.checked)}
              label={
                <span>
                  Irregular pulse detected{" "}
                  <span className="boots-light">(Tick only if irregular)</span>
                </span>
              }
            />

          </ReusableAccordion>
        )}

        {isSectionEnabled(MEASUREMENT_SECTIONS.LIPID_PROFILE) && (
          <ReusableAccordion
            className="lipid-profile-card"
            sectionTitle="Lipid Profile (Cholesterol)"
            hasValidationErrors={Object.values(lipidProfileErrors ?? {}).some(
              Boolean,
            )}
          >
            <VitalityDuplicatedTable
              data={lipidProfileData}
              columns={lipidProfileColumns}
              pagination={false}
              onFieldChange={handleLipidProfileChange}
              errors={lipidProfileErrors}
            />

            <div className="finger-prick-attempts-wrapper">
              <span className="finger-prick-attempts-label">
                How many finger-prick attempts were required to collect the sample?
              </span>
              <div className="finger-prick-radio-group">
                <Form.Check
                  inline
                  type="radio"
                  id="finger-prick-attempts-1"
                  name="finger-prick-attempts"
                  label="1"
                  value={1}
                  checked={fingerPrickAttempts === 1 || fingerPrickAttempts === "1"}
                  onChange={() => setFingerPrickAttempts("1")}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="finger-prick-attempts-2"
                  name="finger-prick-attempts"
                  label="2"
                  value={2}
                  checked={fingerPrickAttempts === 2 || fingerPrickAttempts === "2"}
                  onChange={() => setFingerPrickAttempts("2")}
                />
              </div>
            </div>
          </ReusableAccordion>
        )}

        {isSectionEnabled(MEASUREMENT_SECTIONS.GLUCOSE) && (
          <ReusableAccordion
            className="glucose-card"
            sectionTitle="Glucose (random, non-fasting)"
            hasValidationErrors={Object.values(glucoseErrors ?? {}).some(Boolean)}
          >
            <VitalityDuplicatedTable
              data={glucoseData}
              columns={glucoseColumns}
              pagination={false}
              onFieldChange={handleGlucoseChange}
              errors={glucoseErrors}
            />
          </ReusableAccordion>
          )}
        </div>
        {hasValidationErrors && (
          <VHConfirmationPopup
            warningText={
              <span>
                <b>Please check results entry</b>
                <br />
                Any values highlighted in yellow are outside the expected range
                for this measurement. Please verify the result before selecting
                Continue to submit.
              </span>
            }
            showIcon={false}
          />
        )}
        <Notes
          value={notes}
          onChange={setNotes}
          disabled={false}
          className="mt-4"
        />
        <div className="button-layout">
          <Button
            className="mx-2 my-2"
            label="Continue"
            onClick={handleContinue}
            size="small"
            type="button"
            variant="primary"
            isDisabled={isDisabledContinue}
          />
        </div>
      </PageLayout>
    </LoggedInLayout>
  );
};

export default ClinicalMeasurements;
